/**
 * Populate a generated floor with LIGHTING (Phase 1c-ii), branched by operating
 * state × budget:
 *  - ceiling fixtures per room (gated by that room's breaker),
 *  - windows on the exterior shell admitting sunlight (day only — night sun ≈ 0),
 *  - breaker states (which rooms are powered) from `CONFIG.operating[state]`.
 * Ends by baking `computeLightField`.
 *
 * Reuses the existing light system wholesale (`computeLightField`,
 * `sunlightIntensity`, `LightSource`/breaker model). Pure + deterministic.
 *
 * NOTE (1c-ii): windows are placed per this single floor's shell. Aligning window
 * columns across stacked floors is a Phase 1d (shell) concern.
 */

import { CONFIG } from '../../config';
import { createCamera } from '../camera';
import { createContainer, type Container } from '../container';
import type { DisguiseKind } from '../disguise';
import { createKey, createMop, createUniform, createValuable, type Item } from '../inventory';
import type { BuildProfile, OperatingState } from '../establishment';
import { computeLightField, sunlightIntensity } from '../light';
import { createLock } from '../lock';
import { npcOpenableDoor, setTile, tileAt, type MapData } from '../map';
import { TileType } from '../tiles';
import { findSolidSpot } from './props';
import type { Rect } from './bsp';
import type { RoomType } from './rooms';

/** Nearest floor tile to (x,y) within Chebyshev radius r, or null. */
function nearestFloor(map: MapData, x: number, y: number, r: number): { x: number; y: number } | null {
  for (let rad = 0; rad <= r; rad++) {
    for (let dy = -rad; dy <= rad; dy++) {
      for (let dx = -rad; dx <= rad; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== rad) continue;
        if (tileAt(map, x + dx, y + dy) === TileType.Floor) return { x: x + dx, y: y + dy };
      }
    }
  }
  return null;
}

/** Scatter ceiling fixtures across a room on a grid, tagged with the room id so
 *  its breaker gates them. */
function scatterRoomLights(map: MapData, room: { id: number; x0: number; y0: number; x1: number; y1: number }, spacing: number): void {
  const half = Math.floor(spacing / 2);
  const put = (x: number, y: number): boolean => {
    const spot = nearestFloor(map, x, y, 3);
    if (!spot) return false;
    map.lights.push({
      x: spot.x, y: spot.y, roomId: room.id,
      intensity: CONFIG.light.fixtureIntensity, radius: CONFIG.light.fixtureRadius, mount: 'ceiling',
    });
    return true;
  };
  let placed = 0;
  for (let y = room.y0 + half; y <= room.y1 - 1; y += spacing) {
    for (let x = room.x0 + half; x <= room.x1 - 1; x += spacing) {
      if (put(x, y)) placed++;
    }
  }
  // Every room gets a fixture. On the grid alone, a room narrower or shorter
  // than the spacing gets none at all and stands pitch dark with its breaker ON
  // — which reads as a bug, and makes guards hunker in a lit building.
  if (placed === 0) {
    put(Math.floor((room.x0 + room.x1) / 2), Math.floor((room.y0 + room.y1) / 2));
  }
}

/** A window on the shell at (x,y): make the wall glass + push a natural (sunlight)
 *  source that spills inward. `sun` is the time-of-day intensity (0 at night). */
function addWindow(map: MapData, x: number, y: number, sun: number): void {
  setTile(map, x, y, TileType.Window);
  map.lights.push({
    x, y, roomId: 0,
    intensity: CONFIG.light.windowIntensity * sun,
    radius: CONFIG.light.windowRadius, mount: 'wall', natural: true,
  });
}

/** Place windows along the exterior shell wherever the inward tile is a room
 *  floor (so the light lands inside). Skips corners. */
function placeWindows(map: MapData, cols: number[], rows: number[], sun: number): void {
  const w = map.width;
  const h = map.height;
  const isFloor = (x: number, y: number) => tileAt(map, x, y) === TileType.Floor;
  // Only convert a plain exterior Wall (never the fusebox / a door on the shell).
  const canWindow = (wx: number, wy: number, ix: number, iy: number) =>
    tileAt(map, wx, wy) === TileType.Wall && isFloor(ix, iy);
  for (const x of cols) {
    if (canWindow(x, 0, x, 1)) addWindow(map, x, 0, sun);
    if (canWindow(x, h - 1, x, h - 2)) addWindow(map, x, h - 1, sun);
  }
  for (const y of rows) {
    if (canWindow(0, y, 1, y)) addWindow(map, 0, y, sun);
    if (canWindow(w - 1, y, w - 2, y)) addWindow(map, w - 1, y, sun);
  }
}

/**
 * Light the floor for `operating` state. `spawnRoomId` is forced powered so the
 * player can always see at the entrance.
 */
export function populateLights(
  map: MapData,
  profile: BuildProfile,
  operating: OperatingState,
  spawnRoomId: number,
  rng: () => number,
  windows: { cols: number[]; rows: number[]; enabled: boolean },
): void {
  const op = CONFIG.operating[operating];
  const sun = sunlightIntensity(op.hour);

  if (windows.enabled) placeWindows(map, windows.cols, windows.rows, sun);
  const manned = new Set(
    map.rooms
      .filter((r) => map.stations.some((s) => s.x > r.x0 && s.x < r.x1 && s.y > r.y0 && s.y < r.y1))
      .map((r) => r.id),
  );
  for (const room of map.rooms) {
    scatterRoomLights(map, room, profile.lightSpacing);
    // Breakers: a fraction of rooms are powered (all by day, few at night); the
    // spawn/lobby room is always lit, and so is any room with a MANNED security
    // desk — somebody is sitting there around the clock. At night that makes the
    // security office a lit landmark: easy to find, and exactly where not to go.
    room.lightsOn = room.id === spawnRoomId || manned.has(room.id) || rng() < op.breakersOn;
  }

  computeLightField(map);
}

function randInt(rng: () => number, range: readonly [number, number]): number {
  const [lo, hi] = range;
  return lo + Math.floor(rng() * (hi - lo + 1));
}

/** Mount the fusebox on a wall of `cell` that has room floor just inside it. */
function placeFusebox(map: MapData, cell: Rect): boolean {
  const { x0, y0, x1, y1 } = cell;
  const solidWall = (x: number, y: number) => tileAt(map, x, y) === TileType.Wall;
  const floorIn = (x: number, y: number) => tileAt(map, x, y) === TileType.Floor;
  const walls: Array<[number, number, number, number]> = []; // wallX,wallY, inX,inY
  for (let x = x0 + 1; x <= x1 - 1; x++) {
    walls.push([x, y0, x, y0 + 1]);
    walls.push([x, y1, x, y1 - 1]);
  }
  for (let y = y0 + 1; y <= y1 - 1; y++) {
    walls.push([x0, y, x0 + 1, y]);
    walls.push([x1, y, x1 - 1, y]);
  }
  for (const [wx, wy, ix, iy] of walls) {
    if (solidWall(wx, wy) && floorIn(ix, iy)) {
      setTile(map, wx, wy, TileType.Fusebox);
      map.fusebox = { x: wx, y: wy };
      return true;
    }
  }
  return false;
}

/**
 * Lock a share of the interior doors (`CONFIG.operating[state].lockDensity` —
 * the knob 1a added and 1c never used, which left the whole lockpicking system
 * dark in mission mode).
 *
 * The constraint that makes this safe: NPCs cannot open a locked door
 * (`npcOpenableDoor`), so a careless lock strands a patrol behind it. Every
 * candidate is therefore locked PROVISIONALLY and reverted unless the floor is
 * still fully connected through the doors that remain — the same
 * try-then-verify shape `props.ts` uses for furniture.
 *
 * Keys exist for every lock, in the security office's cabinet: the player can
 * pick a door open, or take the ring off the room that holds it.
 */
export function populateDoorLocks(
  map: MapData,
  profile: BuildProfile,
  operating: OperatingState,
  rng: () => number,
): void {
  const density = CONFIG.operating[operating].lockDensity;
  const spawnRoom = map.rooms.find(
    (r) => map.spawn.x > r.x0 && map.spawn.x < r.x1 && map.spawn.y > r.y0 && map.spawn.y < r.y1,
  );
  // Never lock the way in — starting the run shut inside the lobby is a puzzle
  // nobody asked for.
  const cands = map.doors.filter((d) => {
    if (d.lockId !== null) return false;
    if (!spawnRoom) return true;
    return !(d.x >= spawnRoom.x0 && d.x <= spawnRoom.x1 && d.y >= spawnRoom.y0 && d.y <= spawnRoom.y1);
  });
  for (let i = cands.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [cands[i], cands[j]] = [cands[j]!, cands[i]!];
  }

  const want = Math.round(cands.length * density);
  const keys: Item[] = [];
  let locked = 0;
  for (const door of cands) {
    if (locked >= want) break;
    const sec = Math.max(1, Math.min(profile.lockCeil, 1 + Math.floor(rng() * profile.lockCeil)));
    const lock = createLock({ owner: 'door', ownerId: 0, x: door.x, y: door.y, securityLevel: sec });
    door.lockId = lock.id;
    door.staffKeyed = true; // the people who work here can still get through
    map.locks.push(lock);
    if (!npcConnected(map)) {
      // This one would strand a patrol — put it back.
      door.lockId = null;
      map.locks.pop();
      continue;
    }
    keys.push(createKey(lock.id, 'Door Key'));
    locked++;
  }
  if (keys.length === 0) return;
  // The ring lives where the building would keep it: the security office if it
  // has a cabinet, else any cabinet. NEVER a safe — keys behind the thing you
  // came to crack is a joke, not a puzzle, and a floor with nowhere sensible to
  // hang them simply has none: you always carry picks.
  const cabinets = map.containers.filter((c) => c.glyph !== 'Θ');
  const ring = cabinets.find((c) => {
    const room = map.rooms.find((r) => c.x > r.x0 && c.x < r.x1 && c.y > r.y0 && c.y < r.y1);
    return room?.kind === 'security';
  }) ?? cabinets[0] ?? hangRingOnACabinet(map);
  if (ring) ring.items.push(...keys);
}

/** No lootable cabinet on this floor yet — make one out of a furniture cabinet
 *  so the keys have somewhere to live. Returns null if there's no cabinet at all. */
function hangRingOnACabinet(map: MapData): Container | null {
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      if (tileAt(map, x, y) !== TileType.PropTall) continue;
      if (map.glyphs[y * map.width + x] !== '▤') continue;
      if (map.containers.some((c) => c.x === x && c.y === y)) continue;
      const c = createContainer({ x, y, glyph: '▤', items: [] });
      map.containers.push(c);
      return c;
    }
  }
  return null;
}

/** Can an NPC still reach every room interior from the spawn, given the locks
 *  currently set? Doors an NPC can't open are walls to this flood. */
function npcConnected(map: MapData): boolean {
  const seen = new Uint8Array(map.width * map.height);
  const stack = [map.spawn.y * map.width + map.spawn.x];
  while (stack.length) {
    const idx = stack.pop()!;
    if (seen[idx]) continue;
    seen[idx] = 1;
    const x = idx % map.width;
    const y = Math.floor(idx / map.width);
    const t = tileAt(map, x, y);
    const passable = t === TileType.Floor || t === TileType.PropDecor || t === TileType.Door
      || t === TileType.StairsUp || t === TileType.StairsDown || npcOpenableDoor(map, x, y);
    if (!passable) continue;
    if (x > 0) stack.push(idx - 1);
    if (x < map.width - 1) stack.push(idx + 1);
    if (y > 0) stack.push(idx - map.width);
    if (y < map.height - 1) stack.push(idx + map.width);
  }
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      if (tileAt(map, x, y) !== TileType.Floor) continue;
      if (!seen[y * map.width + x]) return false;
    }
  }
  return true;
}

/** Which furniture is worth searching, and what tends to be in it. Desks and
 *  filing cabinets hold paperwork and petty cash; shelves and counters hold
 *  supplies. Nothing here is locked — a desk drawer is not a safe. */
const SEARCHABLE: ReadonlyArray<{ glyph: string; junkChance: number }> = [
  { glyph: 'π', junkChance: 0.35 },  // desk
  { glyph: '§', junkChance: 0.5 },   // filing cabinet
  { glyph: '≡', junkChance: 0.4 },   // shelving
  { glyph: '╤', junkChance: 0.3 },   // counter
  { glyph: '╥', junkChance: 0.2 },   // table
];

/**
 * Make a share of the furniture searchable. A floor full of desks and shelves
 * was scenery; this turns it into somewhere to look, which is what makes
 * exploring a room worth the exposure.
 *
 * Reuses the whole container path — `createContainer`, `map.containers`,
 * `nearContainer`, E-to-loot — so nothing new is needed to open one.
 */
export function populateFurnitureCaches(
  map: MapData,
  profile: BuildProfile,
  rng: () => number,
): void {
  const chance = CONFIG.gen.searchableFurniture;
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const t = tileAt(map, x, y);
      if (t !== TileType.PropLow && t !== TileType.PropTall) continue;
      const glyph = map.glyphs[y * map.width + x] ?? '';
      const spec = SEARCHABLE.find((sp) => sp.glyph === glyph);
      if (!spec) continue;
      if (map.containers.some((c) => c.x === x && c.y === y)) continue; // already a cache
      if (rng() >= chance) continue;
      const items: Item[] = [];
      if (rng() < spec.junkChance) items.push(createMop());
      else {
        const tier = VALUABLES[Math.min(VALUABLES.length - 1, Math.floor(rng() * profile.lockCeil))]!;
        items.push(createValuable(tier.label, Math.round(tier.value * CONFIG.gen.furnitureValueMult)));
      }
      map.containers.push(createContainer({ x, y, glyph, items }));
    }
  }
}

/** What turns up in a safe, poorest first — indexed by the building's security. */
const VALUABLES: ReadonlyArray<{ label: string; value: number }> = [
  { label: 'Petty cash', value: 250 },
  { label: 'Bearer bond', value: 900 },
  { label: 'Data shard', value: 1800 },
  { label: 'Signet ledger', value: 3200 },
  { label: 'Prototype core', value: 6000 },
];

/** Rooms worth watching, best first: circulation you must cross, then the
 *  places the payload lives. */
const WATCHED_ROOMS: readonly RoomType[] = ['corridor', 'lobby', 'security', 'server', 'records'];

/**
 * Mount security cameras (Phase 8). Each goes on a WALL tile with room floor
 * just inside it, looking in — the same mount test the fusebox uses. Count comes
 * from the budget: a dirt-poor office has none at all, which is a real
 * difference in how a floor plays rather than just a number.
 */
export function populateCameras(
  map: MapData,
  cells: Rect[],
  types: RoomType[],
  security: number,
  rng: () => number,
): void {
  const want = CONFIG.securityCamera.perFloorBySecurity[Math.max(0, Math.min(4, security - 1))] ?? 0;
  if (want <= 0) return;
  // Candidate rooms in priority order, so the few cameras a cheap building has
  // end up on the routes that matter.
  const order: number[] = [];
  for (const kind of WATCHED_ROOMS) {
    cells.forEach((_c, i) => { if (types[i] === kind) order.push(i); });
  }
  let placed = 0;
  for (const i of order) {
    if (placed >= want) break;
    const mount = cameraMount(map, cells[i]!, rng);
    if (!mount) continue;
    map.cameras.push(createCamera(mount.x, mount.y, mount.dx, mount.dy));
    map.glyphs[mount.y * map.width + mount.x] = '⊙';
    placed++;
  }
}

/** A wall tile of `cell` with floor just inside it, plus the inward direction. */
function cameraMount(
  map: MapData,
  cell: Rect,
  rng: () => number,
): { x: number; y: number; dx: number; dy: number } | null {
  const { x0, y0, x1, y1 } = cell;
  const cands: Array<{ x: number; y: number; dx: number; dy: number }> = [];
  for (let x = x0 + 1; x <= x1 - 1; x++) {
    cands.push({ x, y: y0, dx: 0, dy: 1 });
    cands.push({ x, y: y1, dx: 0, dy: -1 });
  }
  for (let y = y0 + 1; y <= y1 - 1; y++) {
    cands.push({ x: x0, y, dx: 1, dy: 0 });
    cands.push({ x: x1, y, dx: -1, dy: 0 });
  }
  for (let i = cands.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [cands[i], cands[j]] = [cands[j]!, cands[i]!];
  }
  for (const c of cands) {
    if (tileAt(map, c.x, c.y) !== TileType.Wall) continue;          // a plain wall
    if (tileAt(map, c.x + c.dx, c.y + c.dy) !== TileType.Floor) continue; // looking at floor
    if (map.cameras.some((cam) => cam.x === c.x && cam.y === c.y)) continue;
    return c;
  }
  return null;
}

/** Which uniform hangs in which room's lockers. */
const LOCKER_UNIFORM: Partial<Record<RoomType, DisguiseKind>> = {
  security: 'guard',   // the gear locker in the security office
  utility: 'janitor',  // coveralls by the mop sink
  storage: 'worker',
  break: 'worker',     // people leave a jacket in the break room
};

/**
 * Hang uniforms in the building's lockers. A cabinet already furnishes these
 * rooms; this makes ONE of them per eligible room a lootable container with a
 * uniform in it, so a disguise is something you can find by exploring rather
 * than only by leaving a body behind. The security office's gear locker is the
 * one that's locked.
 */
export function populateUniforms(
  map: MapData,
  cells: Rect[],
  types: RoomType[],
  profile: BuildProfile,
  rng: () => number,
): void {
  cells.forEach((c, i) => {
    const kind = LOCKER_UNIFORM[types[i]!];
    if (kind === undefined) return;
    if (rng() > CONFIG.disguise.lockerChance) return;
    // Reuse a cabinet the furnisher already placed in this room.
    for (let y = c.y0 + 1; y <= c.y1 - 1; y++) {
      for (let x = c.x0 + 1; x <= c.x1 - 1; x++) {
        if (tileAt(map, x, y) !== TileType.PropTall) continue;
        if (map.glyphs[y * map.width + x] !== '▤') continue;
        if (map.containers.some((ct) => ct.x === x && ct.y === y)) continue;
        const locker = createContainer({ x, y, glyph: '▤', items: [createUniform(kind)] });
        // The GEAR locker in a security office is locked — the most powerful
        // uniform in the building shouldn't be lying open. A jacket in the break
        // room is just a jacket.
        if (kind === 'guard') {
          const sec = Math.max(1, Math.min(profile.lockCeil, 2));
          const lock = createLock({ owner: 'container', ownerId: locker.id, x, y, securityLevel: sec });
          locker.lockId = lock.id;
          map.locks.push(lock);
        }
        map.containers.push(locker);
        return;
      }
    }
  });
}

/**
 * Security fixtures: the fusebox (in the utility room) and budget-scaled safes
 * (a PropTall `Θ` tile carrying a locked `Container`) seated in high-value rooms.
 * Run BEFORE lighting so the solid safe tiles occlude light correctly.
 */
export function populateSecurity(
  map: MapData,
  cells: Rect[],
  types: RoomType[],
  profile: BuildProfile,
  rng: () => number,
): void {
  // Fusebox: utility room, else security, else the first non-lobby room.
  const fbIdx = [types.indexOf('utility'), types.indexOf('security')].find((i) => i >= 0)
    ?? cells.findIndex((_c, i) => types[i] !== 'lobby');
  if (fbIdx >= 0) placeFusebox(map, cells[fbIdx]!);

  // Safes in high-value rooms.
  const safeKinds = new Set<RoomType>(['records', 'security', 'private-office', 'server']);
  const candidates = cells.map((_c, i) => i).filter((i) => safeKinds.has(types[i]!));
  const count = randInt(rng, profile.safes);
  for (let n = 0; n < count && candidates.length > 0; n++) {
    const roomI = candidates[Math.floor(rng() * candidates.length)]!;
    const spot = findSolidSpot(map, cells[roomI]!, rng);
    if (!spot) continue;
    setTile(map, spot.x, spot.y, TileType.PropTall);
    map.glyphs[spot.y * map.width + spot.x] = 'Θ';
    // A safe with nothing in it isn't a safe. Contents scale with the security
    // that's guarding them, so a hard building is a richer one.
    const loot = Array.from({ length: 1 + Math.floor(rng() * 2) }, () => {
      const tier = VALUABLES[Math.min(VALUABLES.length - 1, Math.floor(rng() * profile.lockCeil))]!;
      return createValuable(tier.label, tier.value);
    });
    const container = createContainer({ x: spot.x, y: spot.y, glyph: 'Θ', items: loot });
    const sec = Math.max(1, Math.min(profile.lockCeil, 1 + Math.floor(rng() * profile.lockCeil)));
    const lock = createLock({ owner: 'container', ownerId: container.id, x: spot.x, y: spot.y, securityLevel: sec });
    container.lockId = lock.id;
    map.containers.push(container);
    map.locks.push(lock);
  }
}
